import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Check } from "lucide-react";
import { Reveal, Tag, Heading } from "./Reveal";

const DETAILS = ["The grout line matters.", "The cabinet alignment matters.", "The transition between two materials matters.", "The trim matters."];

const FullBleed = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="relative h-[70vh] sm:h-[90vh] overflow-hidden">
      <motion.img
        src="/images/kitchen-farmhouse.png"
        alt="A warm farmhouse kitchen renovated by Construction Alliance"
        className="absolute inset-0 h-[116%] w-full object-cover -top-[8%]"
        style={{ y }}
        data-testid="home-respect-image"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 mx-auto max-w-[1600px] px-5 sm:px-8 pb-10 sm:pb-16">
        <Reveal>
          <p className="font-display font-semibold text-bone tracking-[-0.03em] leading-[0.9] text-[clamp(2.6rem,8vw,8.5rem)]" data-testid="love-my-house-quote">
            "Damn. I love<br />my house."
          </p>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-bone/60">What we want you thinking when we leave</p>
        </Reveal>
      </div>
    </div>
  );
};

export const HomeRespect = () => (
  <>
    <section data-testid="home-respect-section">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-24 lg:py-40 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <Reveal className="lg:col-span-6">
          <Tag>Your home isn't a job site</Tag>
          <Heading className="mt-6">It's your home.<br /><span className="text-ink/35">We never forget that.</span></Heading>
        </Reveal>
        <Reveal className="lg:col-span-5 lg:col-start-8 lg:pt-4 space-y-5 text-base sm:text-lg text-ink/70 leading-relaxed" delay={0.1}>
          <p>Construction is disruptive by nature. Strangers are coming into the place where you wake up, eat dinner, raise your kids, entertain your friends and unwind at the end of the day. We consider that space sacred.</p>
          <p>So how we work matters just as much as what we build. We think about the dust. The noise. The mess. The schedule. Where you're going to make coffee tomorrow morning.</p>
          <p className="text-ink font-medium">We communicate. We protect your space. We clean up after ourselves. We obsess over the little things.</p>
        </Reveal>
      </div>
      <FullBleed />
    </section>

    <section className="py-24 lg:py-40" data-testid="craftsmanship-section">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <Reveal className="lg:col-span-6">
          <div className="relative">
            <img
              src="/images/kitchen-bright.png"
              alt="A bright white kitchen with precise cabinet alignment and clean lines"
              className="w-full aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5] object-cover"
              data-testid="craftsmanship-image"
            />
            <div className="absolute bottom-0 right-0 bg-ink text-bone px-6 py-5 max-w-[260px]">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-teal">The eighth of an inch</p>
              <p className="mt-2 font-display font-medium text-lg leading-snug">You might not notice. We do.</p>
            </div>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.1}>
          <Tag>Built right. Finished beautifully.</Tag>
          <Heading className="mt-6 text-[2.2rem] sm:text-5xl lg:text-6xl">
            There's construction that gets the job done. <span className="text-teal">Then there's craftsmanship.</span>
          </Heading>
          <p className="mt-8 text-base sm:text-lg text-ink/70 leading-relaxed">At Construction Alliance, aesthetics matter.</p>
          <ul className="mt-6 divide-y divide-ink/10 border-y border-ink/10">
            {DETAILS.map((d) => (
              <li key={d} className="flex items-center gap-4 py-4 font-display font-medium text-lg text-ink">
                <Check className="h-4 w-4 text-teal shrink-0" strokeWidth={3} />
                {d}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-base text-ink/70 leading-relaxed">
            The thing that's off by an eighth of an inch that you might never consciously notice? <span className="text-ink font-semibold">We notice it.</span> We're perfectionists about the details because those details are what make a finished space feel finished.
          </p>
        </Reveal>
      </div>
    </section>
  </>
);
